import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import trackLead from "./trackLead";

const formName = "customer-account-request";
const required = "Please complete this field.";

function CustomerRequestForm() {
  const { register, handleSubmit, setFocus, formState: { errors } } = useForm();
  const [state, setState] = useState("idle");
  const inFlight = useRef(false);
  const formRef = useRef(null);
  const statusRef = useRef(null);

  useEffect(() => {
    if (state === "sent" || state === "error") statusRef.current?.focus();
  }, [state]);

  const submit = handleSubmit(async values => {
    if (inFlight.current || state === "sent") return;
    inFlight.current = true;
    setState("sending");
    try {
      const formData = new FormData(formRef.current);
      for (const name of ["first_name", "last_name", "email", "phone", "organization"]) {
        formData.set(name, String(values[name] || "").trim());
      }
      // Intake stays separate from /api/users/signup, which must not provision access before an operations call.
      const response = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      if (!response.ok) throw new Error(`Submission failed (${response.status})`);
      trackLead(formName);
      setState("sent");
    } catch (error) {
      setState("error");
    } finally {
      inFlight.current = false;
    }
  }, invalid => {
    const first = Object.keys(invalid)[0];
    if (first) setFocus(first);
  });

  const field = (name, label, options = {}) => {
    const id = `request-${name.replace(/_/g, "-")}`;
    const type = name === "email" ? "email" : name === "phone" ? "tel" : "text";
    const autoComplete = { first_name: "given-name", last_name: "family-name", email: "email", phone: "tel", organization: "organization" }[name];
    const description = [name === "phone" ? "request-phone-help" : null, errors[name] ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;
    return <div className="field" key={name}>
      <label htmlFor={id}>{label} {options.required ? <span className="request-required" aria-hidden="true">*</span> : <span className="request-optional">Optional</span>}</label>
      <input id={id} name={name} type={type} autoComplete={autoComplete} inputMode={name === "email" ? "email" : name === "phone" ? "tel" : undefined} maxLength={options.maxLength} aria-required={Boolean(options.required)} aria-invalid={Boolean(errors[name])} aria-describedby={description} {...register(name, options)} />
      {name === "phone" && <small id="request-phone-help">Include a country code if outside the United States.</small>}
      {errors[name] && <small className="request-error" id={`${id}-error`} role="alert">{errors[name].message}</small>}
    </div>;
  };

  return <form ref={formRef} name={formName} method="POST" action="/__forms.html" data-netlify="true" netlify-honeypot="bot-field" onSubmit={submit} noValidate>
    <input type="hidden" name="form-name" value={formName} />
    <input type="hidden" name="subject" value="Customer account request" />
    <input type="hidden" name="request_type" value="customer_account_request" />
    <input type="hidden" name="source" value="public_signup" />
    <p className="request-honeypot" aria-hidden="true"><label>Leave this field empty <input name="bot-field" tabIndex="-1" autoComplete="off" /></label></p>
    <div className="request-form-row">
      {field("first_name", "First name", { required, maxLength: 120 })}
      {field("last_name", "Last name", { required, maxLength: 120 })}
    </div>
    <div className="request-form-row">
      {field("email", "Email address", { required, maxLength: 254, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Please enter a valid email address." } })}
      {field("phone", "Phone number", { required, maxLength: 25, validate: value => { const digits = (value || "").replace(/\D/g, ""); return (digits.length >= 7 && digits.length <= 15) || "Please enter a complete phone number."; } })}
    </div>
    {field("organization", "Company or flight department", { maxLength: 160 })}
    <p className="request-privacy">We’ll use your details to discuss your request and set up access if appropriate. This form does not enroll you in job-status text messages. See our <Link to="/privacy-policy">Privacy Notice</Link>.</p>
    <button className="button request-submit" type="submit" disabled={state === "sending" || state === "sent"}>{state === "sending" ? "Sending…" : state === "sent" ? "Request sent" : "Request a customer account"} <span aria-hidden="true">→</span></button>
    {state === "sent" && <p ref={statusRef} className="request-status" role="status" tabIndex="-1">Request received. Our team will contact you by phone or email before setting up an account.</p>}
    {state === "error" && <p ref={statusRef} className="request-status request-status-error" role="alert" tabIndex="-1">Your request could not be sent. Please try again or call +1 855-500-0538.</p>}
  </form>;
}

export default function CustomerRequestContent() {
  return <main id="main" className="customer-request">
    <section className="request-hero"><div className="wrap request-intro"><div><p className="eyebrow">FOR FLIGHT DEPARTMENTS &amp; AIRCRAFT OPERATORS</p><h1>Start with a conversation.</h1><p>Request a LiveTakeoff customer account. We’ll call to learn about your operation and how you’d like to manage aircraft care before creating access.</p></div><div className="request-intro-note"><span aria-hidden="true">01 / 02 / 03</span><strong>Request access → Connect with our team → Get your account</strong></div></div></section>
    <section className="wrap request-layout" aria-label="Customer account request"><div className="request-card" id="account-request"><div className="request-card-heading"><p className="eyebrow">REQUEST CUSTOMER ACCESS</p><h2>Tell us how to reach you.</h2><p>This takes about a minute. Submitting a request does not create a login.</p></div><CustomerRequestForm /></div>
      <aside className="request-aside"><div className="request-next-card"><p className="eyebrow">WHAT HAPPENS NEXT</p><ol><li><span>01</span><div><strong>We review your request.</strong><p>Our team confirms the best contact for your operation.</p></div></li><li><span>02</span><div><strong>We have a short call.</strong><p>Tell us where you operate and what support you need.</p></div></li><li><span>03</span><div><strong>We set up access.</strong><p>After that conversation, we’ll guide you through account setup.</p></div></li></ol></div><div className="request-other-paths"><p>Already have an account? <Link to="/login#sign-in">Sign in ↗</Link></p><p>Aircraft detailing provider? <Link to="/vendors/apply">Apply to join our network ↗</Link></p><p>Have a question first? <Link to="/shared/contact">Contact us ↗</Link></p></div></aside>
    </section>
  </main>;
}
